import { useAppDispatch, useAppSelector } from 'app/store';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { Flex } from '@chakra-ui/layout';
import {
  Box,
  Card,
  IconButton,
  VStack,
  Text,
  Tooltip,
  Heading,
  Skeleton,
} from '@chakra-ui/react';
import { useResizableBlock } from 'app/utils/hooks/useResizableBlock';
import { getProfileBarSettings } from 'app/store/Settings/selectors';
import { setProfileBarSettings } from 'app/store/Settings/slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Profile } from 'types/profile';
import { fetchProfileEffect } from 'app/store/Profile/effects';
import { useIsMounted } from 'app/utils/hooks';
import { getStyles } from './styles';
import { HEADER_HEIGHT } from '../HeaderBar';
import { BackgroundImage } from './BackgroundImage';
import {
  getProfileData,
  getRepresentativesDate,
} from '../ContactCardDialog/utils';

export const ProfileBar = () => {
  const dispatch = useAppDispatch();
  const playerHeight = useAppSelector(getPlayerHeight);
  const profileBar = useAppSelector(getProfileBarSettings);
  const height = `calc(100vh - ${HEADER_HEIGHT}px - ${playerHeight}px)`;

  const {
    panelRef,
    cursor,
    handleMouseDown,
    isDragging,
    forceCompactMode,
  } = useResizableBlock('co-writer-bar', 'rtl', false);

  const {
    rootSx,
    wrapperSx,
    contentSx,
    cardSx,
    sidebarResizeLineSx,
    closeIconSx,
    fullNameSx,
  } = getStyles({
    forceCompactMode,
    isDragging,
  });

  const titleRef = useRef<HTMLParagraphElement>(null);
  const title = profileBar?.collaborator.profile?.displayName;
  const isTitleOverflow =
    (titleRef?.current?.scrollWidth || 0) >
    (titleRef?.current?.clientWidth || 0);

  const shared = false;
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<Profile>();
  const profileData = getProfileData(isLoading, profile);
  const fetchProfileDetails = useCallback(async () => {
    if (!profileBar) {
      return;
    }

    try {
      setIsLoading(true);
      setProfile(undefined);

      const getProfile = () =>
        dispatch(
          fetchProfileEffect({
            profileGuid: profileBar.collaborator.profile.guid,
          })
        ).unwrap();

      setProfile(shared ? profileBar.collaborator.profile : await getProfile());
      if (isMounted()) {
        setIsLoading(false);
      }
    } catch {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [profileBar, shared, isMounted, dispatch]);
  const representativesData = getRepresentativesDate(profile?.representatives);

  useEffect(() => {
    if (profileBar) {
      fetchProfileDetails();
    }
  }, [fetchProfileDetails, profileBar]);

  if (!profileBar) {
    return null;
  }

  return (
    <Flex ref={panelRef} sx={rootSx}>
      <Flex sx={wrapperSx}>
        <Box
          onMouseDown={handleMouseDown}
          cursor={forceCompactMode ? 'default' : cursor}
          sx={sidebarResizeLineSx}
        >
          <Box />
        </Box>
        <VStack sx={contentSx} height={height} spacing={1}>
          <Card sx={cardSx} data-testid="right-bar-card">
            <Flex
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Flex flex={1} alignItems="center" justifyContent="space-between">
                <Text color="textSubdued" fontWeight={500}>
                  Profile Information
                </Text>
                <IconButton
                  sx={closeIconSx}
                  variant="ghost"
                  icon={<CloseIcon />}
                  aria-label="close-rigth-bar"
                  onClick={() => dispatch(setProfileBarSettings())}
                />
              </Flex>
            </Flex>

            <Box
              pt={2}
              pb={4}
              mb={4}
              borderBottom="1px solid"
              borderColor="borderBase"
            >
              <BackgroundImage collaborator={profileBar.collaborator} />
              <Tooltip
                label={title}
                sx={{ wordBreak: 'break-all' }}
                placement="top"
                isDisabled={!isTitleOverflow}
                hasArrow
              >
                <Text
                  ref={titleRef}
                  as="h2"
                  data-testid="dialog-title"
                  mt={2}
                  sx={fullNameSx}
                >
                  {title}
                </Text>
              </Tooltip>
            </Box>

            {isLoading ? (
              <VStack gap={1}>
                <Skeleton w="full" h={12} />
                <Skeleton w="full" h={12} />
                <Skeleton w="full" h={12} />
              </VStack>
            ) : profileData.length === 0 && representativesData.length === 0 ? (
              <VStack textAlign="center" pt={6}>
                <Heading as="h6" size="sm">
                  No Collaborator information
                </Heading>
                <Text>
                  Collaborator has not added additional
                  <br />
                  profile information
                </Text>
              </VStack>
            ) : (
              <>
                {profileData.map(({ key, label, value }) => (
                  <Flex key={key} minH={12}>
                    <Text w={100} color="textSubdued">
                      {label}
                    </Text>
                    <Box>
                      <Text w="fit-content">{value.primary}</Text>
                      {value.secondary && (
                        <Text fontSize="xs" color="textBase" w="fit-content">
                          {value.secondary}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                ))}
                {representativesData.map(
                  ({ name, email, role, company, publisherIpi }) => (
                    <Flex key={name} my={1}>
                      <Text w={100} color="textSubdued">
                        {role}
                      </Text>
                      <Box w="fit-content">
                        <Text>{name}</Text>
                        <Text fontSize="xs" color="textSubdued">
                          {company}
                        </Text>
                        {publisherIpi && (
                          <Text fontSize="xs" color="textSubdued">
                            IPI: {publisherIpi}
                          </Text>
                        )}
                        <Text fontSize="xs" color="textSubdued">
                          {email}
                        </Text>
                      </Box>
                    </Flex>
                  )
                )}
              </>
            )}
          </Card>
        </VStack>
      </Flex>
    </Flex>
  );
};
